import React from 'react';
import { featurePanelState, formatWorkedDuration } from '../../utils/timeOnSystem';

const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);
const TOP_FEATURES = 12;

function Bar({ value, max }) {
  const width = max > 0 && value > 0 ? Math.max(3, (value / max) * 100) : 0;
  return (
    <span className="block h-2 w-full max-w-[180px] rounded-full bg-gray-100">
      <span className="block h-2 rounded-full bg-[#1c5f4c]" style={{ width: `${width}%` }} />
    </span>
  );
}

function Note({ children }) {
  return (
    <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
      {children}
    </p>
  );
}

/**
 * Which parts of Housingram people spend their time in.
 *
 * Areas are ranked by how many people used them — the capacity question — and
 * features by people, then time. Feature time is raw activity without the grace
 * added to total time, and a slot in two features counts for both, so the note
 * says why these do not add up to the headline figure.
 */
const FeatureUsagePanel = ({ data, isLoading, isError }) => {
  if (isLoading) {
    return (
      <section className="mt-6 rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-gray-500">
        Loading feature usage…
      </section>
    );
  }
  if (isError) {
    return (
      <section className="mt-6 rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-red-600">
        Could not load feature usage. Try again in a moment.
      </section>
    );
  }
  const state = featurePanelState(data);
  if (state === 'hidden') return null;

  const areas = data.areas || [];
  const allFeatures = data.features || [];
  const features = allFeatures.slice(0, TOP_FEATURES);
  const maxAreaUsers = areas.reduce((m, a) => Math.max(m, a.users || 0), 0);
  const maxFeatureUsers = features.reduce((m, f) => Math.max(m, f.users || 0), 0);

  return (
    <section className="mt-6">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">Features used</h2>
        {state === 'ready' ? (
          <span className="text-xs text-gray-500">
            {data.users_with_features} {plural(data.users_with_features, 'person', 'people')}{' '}
            used at least one feature
          </span>
        ) : null}
      </div>

      {state === 'unavailable' ? (
        <Note>
          Feature tracking starts once the latest backend update is deployed. Usage is
          recorded from that point on — earlier activity cannot be split by feature.
        </Note>
      ) : state === 'empty' ? (
        <Note>No feature activity was recorded in this period.</Note>
      ) : (
        <>
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full min-w-[420px] border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5 text-left font-medium">Area</th>
                    <th className="px-4 py-2.5 text-right font-medium">People</th>
                    <th className="px-4 py-2.5 text-left font-medium" />
                    <th className="px-4 py-2.5 text-right font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map((area) => (
                    <tr key={area.area} className="border-t border-gray-100">
                      <td className="px-4 py-2.5 text-gray-900">{area.area}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                        {area.users}
                      </td>
                      <td className="px-4 py-2.5">
                        <Bar value={area.users} max={maxAreaUsers} />
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                        {formatWorkedDuration(area.minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full min-w-[420px] border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5 text-left font-medium">Feature</th>
                    <th className="px-4 py-2.5 text-right font-medium">People</th>
                    <th className="px-4 py-2.5 text-left font-medium" />
                    <th className="px-4 py-2.5 text-right font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((feature) => (
                    <tr key={feature.feature} className="border-t border-gray-100">
                      <td className="px-4 py-2.5">
                        <div className="text-gray-900">{feature.label}</div>
                        <div className="text-xs text-gray-400">{feature.area}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                        {feature.users}
                      </td>
                      <td className="px-4 py-2.5">
                        <Bar value={feature.users} max={maxFeatureUsers} />
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                        {formatWorkedDuration(feature.minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-2 max-w-3xl text-xs leading-relaxed text-gray-500">
            {allFeatures.length > TOP_FEATURES
              ? `Showing the ${TOP_FEATURES} most used of ${allFeatures.length} features. `
              : ''}
            A five-minute slot spent in two features counts toward both, so feature times
            add up to more than total time. Feature time also leaves out the grace added
            to total time, so it reads a little lower.
          </p>
        </>
      )}
    </section>
  );
};

/** One person's most used features, for the drill-down. */
export function UserFeatureList({ data, isLoading, isError }) {
  const features = data?.features || [];
  const more = (data?.feature_count || 0) - features.length;
  return (
    <div className="mt-5 border-t border-gray-100 pt-4">
      <h3 className="text-sm font-semibold text-gray-900">Most used features</h3>
      {isLoading ? (
        <div className="py-4 text-center text-sm text-gray-500">Loading features…</div>
      ) : isError ? (
        <div className="py-4 text-center text-sm text-red-600">
          Could not load features. Try again in a moment.
        </div>
      ) : data && data.available === false ? (
        <p className="mt-2 text-xs text-gray-500">
          Feature tracking starts once the latest backend update is deployed.
        </p>
      ) : !features.length ? (
        <p className="mt-2 text-xs text-gray-500">No feature activity in this period.</p>
      ) : (
        <>
          <ul className="mt-2 space-y-1.5">
            {features.map((feature) => (
              <li
                key={feature.feature}
                className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-900">
                  {feature.label}
                  <span className="ml-2 text-xs text-gray-400">{feature.area}</span>
                </span>
                <span className="tabular-nums text-gray-600">
                  {formatWorkedDuration(feature.minutes)}
                </span>
              </li>
            ))}
          </ul>
          {more > 0 ? (
            <p className="mt-1 text-xs text-gray-400">
              and {more} more {plural(more, 'feature')}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

export default FeatureUsagePanel;
